import React, { useEffect, useState } from 'react';
import { useTranslation } from '@shared/core';
import { pxToRem } from '@withjoy/joykit/theme';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { SkeletonThumbnail } from '@shared/components/Skeleton';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { Flex, Box, BoxProps, TextV2, CloseButton, ButtonV2 } from '@withjoy/joykit';
import { Category } from '@graphql/generated';

import { useCustomPageTelemetry } from '../../../../Custom.telemetry';
import { RemoveScroll, mapSizeVariants } from './AccommodationsMapV2.styles';
import { AccommodationCombined, AccommodationCombinedType } from '../Accommodations/Accommodation.types';
import { DirectionsMap } from '@shared/components/DirectionsMap';
import { button1ToButtonsOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { useScaleGuestSiteFontSize } from '@apps/guest/components/GuestSiteTypographyOverride/hooks/useScaleGuestSiteFontSize';
import { useFeatureValue, useIsFeatureEnabled } from '@shared/core/featureFlags';
import { EventVenue } from '@shared/utils/eventPlace';
import { closeButtonOverrides } from '@shared/components/ResponsiveDialog/ResponsiveDialog.styles';
import { isInIframe } from '@shared/utils/isInIframe';
import { withWindow } from '@shared/utils/withWindow';
import { AiBorderBox } from '@shared/components/AiBorderBox';
import { LottiePlayer } from '@shared/components';
import { MAP_ANIMATION_ICON_URL } from '@shared/components/JoyInteractableMap/MapPin.constants';

interface AccommodationsMapV2Props {
  pageTitle?: string;
  venueInfo: EventVenue;
  accommodationsCombined: AccommodationCombined[];
  showFullScreenMap: boolean;
  isBrannanLayout?: boolean;
  locationBeingHovered?: string;
  borderRadius?: BoxProps['borderRadius'];
  height?: BoxProps['height'];
  hideExploreCta?: boolean;
  hideDirection?: boolean;
  onAccommodationsConsoleOpen?: () => void;
  onMapItemClick?: (hotel: AccommodationCombined) => void;
}

const AccommodationsMapV2: React.FC<AccommodationsMapV2Props> = ({
  pageTitle,
  venueInfo,
  accommodationsCombined,
  showFullScreenMap,
  isBrannanLayout,
  locationBeingHovered,
  borderRadius,
  height,
  hideExploreCta,
  hideDirection,
  onAccommodationsConsoleOpen,
  onMapItemClick,
}) => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const isMobile = useMediaQuery(theme => theme.mediaQueries.between(0, { breakpointAlias: 'sm2' }));

  const { t } = useTranslation('guestSiteCustom');
  const accTrans = t('accommodations');
  const telemetry = useCustomPageTelemetry();
  const { scaleFontSize } = useScaleGuestSiteFontSize();
  const fontPackExperimentEnabled = useFeatureValue('fontPackExperiment').value === 'treatment';
  const [isTravelAccommodationsConsoleV2Enabled] = useIsFeatureEnabled('travelAccommodationsConsoleV2');
  const [isAccommodationsExpandedMapEnabled] = useIsFeatureEnabled('accommodationsExpandedMap');

  const originList =
    accommodationsCombined.map(accommodation => ({
      index: accommodation.index,
      id: accommodation.id,
      lat: accommodation.latitude ?? 0,
      lng: accommodation.longitude ?? 0,
      hotel: accommodation,
    })) || [];

  const mapSizeVariantsResult = mapSizeVariants(isBrannanLayout, isAccommodationsExpandedMapEnabled);
  const mapSize = isFullScreen ? mapSizeVariantsResult.fullscreen : mapSizeVariantsResult.normalscreen;

  const toggleFullScreen = useEventCallback((fullscreen: boolean) => {
    setIsFullScreen(fullscreen);
  });

  useEffect(() => {
    setIsFullScreen(showFullScreenMap);
  }, [showFullScreenMap]);

  const handleMapExpanded = () => {
    telemetry.mapExpanded('accommodations');
  };

  const handleMapContracted = () => {
    telemetry.mapContracted('accommodations');
  };

  const onMapClick = useEventCallback(() => {
    telemetry.accommodationsMapClicked('accommodations');
  });

  const handleMarkerClick = useEventCallback((hotelId: string, ctaUrl?: string) => {
    const hotel = accommodationsCombined.find(acc => acc.id === hotelId);
    telemetry.mapMarkerClicked('accommodations', {
      hotelId,
      hotelName: hotel?.displayName || undefined,
      from: 'miniMap',
    });

    if (isTravelAccommodationsConsoleV2Enabled) {
      if (hotel?.type === AccommodationCombinedType.Custom && hotel.customUrl) {
        withWindow(global => global.open(ctaUrl, '_blank'));
      } else if (hotel) {
        onMapItemClick?.(hotel);
      }
    } else if (ctaUrl) {
      withWindow(global => global.open(ctaUrl, '_blank'));
    }
  });

  return (
    <>
      {isFullScreen && <RemoveScroll />}
      <Flex
        width={'100%'}
        height={'100%'}
        margin={isBrannanLayout && isMobile ? 'auto' : 'inherit'}
      >
        <Flex
          {...mapSize}
          {...(height && { height })}
          borderRadius={isFullScreen || isMobile ? 0 : borderRadius ? borderRadius : isBrannanLayout && !isAccommodationsExpandedMapEnabled ? pxToRem(24) : pxToRem(12)}
          overflow="hidden"
        >
          <DirectionsMap
            destination={{ lat: venueInfo?.latitude ?? 0, lng: venueInfo?.longitude ?? 0, title: accTrans.venue() }}
            originList={originList}
            locationBeingHovered={locationBeingHovered}
            gestureHandling={isFullScreen || isInIframe() ? 'greedy' : 'cooperative'}
            onMapClick={onMapClick}
            onOriginClick={handleMarkerClick}
            hideDirection={hideDirection}
            isAnimate={isAccommodationsExpandedMapEnabled && isBrannanLayout}
          >
            {isFullScreen && pageTitle && !isMobile && (
              <Box
                padding={6}
                width="100%"
                textAlign="center"
                position="absolute"
                backgroundColor="white"
              >
                <TextV2
                  wordBreak="break-word"
                  marginX={'0.24rem'}
                  typographyVariant="display4"
                  color="mono12"
                  fontSize={{ _: pxToRem(40), sm2: pxToRem(48) }}
                  fontWeight={{ _: 52, sm2: 62.4 }}
                >
                  {pageTitle}
                </TextV2>
              </Box>
            )}
            {isFullScreen && (
              <CloseButton
                overrides={{
                  Root: { props: { ...closeButtonOverrides.Root?.props, position: 'absolute', right: 7, top: 7, backgroundColor: 'white' } },
                  Icon: { props: { ...closeButtonOverrides.Icon?.props } },
                }}
                onClick={() => {
                  toggleFullScreen(!isFullScreen);
                  isFullScreen ? handleMapContracted() : handleMapExpanded();
                }}
              />
            )}
            {!isFullScreen && (!hideExploreCta || isTravelAccommodationsConsoleV2Enabled) && (
              <GuestSiteTypographyOverride
                override={button1ToButtonsOverride}
                defaultFontFamily="Inter UI"
              >
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  position="absolute"
                  bottom={7}
                  left="50%"
                  transform="translate(-50%, 0)"
                >
                  <AiBorderBox>
                    <ButtonV2
                      color="mono14"
                      startIcon={
                        <Box
                          maxWidth={7}
                          maxHeight={7}
                        >
                          <LottiePlayer
                            play={true}
                            loop
                            lottieUrl={MAP_ANIMATION_ICON_URL}
                          />
                        </Box>
                      }
                      paddingY={0}
                      minWidth={pxToRem(220)}
                      paddingX={5}
                      _hover={{
                        opacity: 0.8,
                      }}
                      variant="ghost"
                      typographyVariant="button1"
                      fontSize={scaleFontSize(pxToRem(15), Category.BUTTONS)}
                      {...(!fontPackExperimentEnabled && { fontFamily: 'Inter UI' })}
                      fontWeight={600}
                      height={pxToRem(32)}
                      onClick={() => {
                        telemetry.exploreOnMapClicked('accommodations');
                        if (isTravelAccommodationsConsoleV2Enabled && onAccommodationsConsoleOpen) {
                          onAccommodationsConsoleOpen();
                          return;
                        }
                        toggleFullScreen(!isFullScreen);
                        isFullScreen ? handleMapContracted() : handleMapExpanded();
                      }}
                    >
                      {accTrans.exploreOnMap()}
                    </ButtonV2>
                  </AiBorderBox>
                </Flex>
              </GuestSiteTypographyOverride>
            )}
          </DirectionsMap>
        </Flex>
      </Flex>
    </>
  );
};

AccommodationsMapV2.displayName = 'AccommodationsMapV2';

const AccommodationsMapV2Skeleton: React.FC<{
  isBrannanLayout?: boolean;
  isExpandedLayout?: boolean;
}> = ({ isBrannanLayout, isExpandedLayout }) => {
  // For expanded layout, use full height and width
  if (isExpandedLayout) {
    return (
      <SkeletonThumbnail
        overflow="hidden"
        height="100%"
        width="100%"
        marginBottom={0}
        overrides={{
          PlaceholderRoot: {
            props: {
              width: '100%',
              height: '100%',
              backgroundColor: 'white',
              borderRadius: '12px !important',
            },
          },
        }}
      />
    );
  }

  // Original skeleton for normal layout
  return (
    <Flex
      width="100%"
      display={{ _: 'flex', sm2: 'flex' }}
      maxWidth={isBrannanLayout ? { _: '520px', sm2: '100%' } : {}}
    >
      <SkeletonThumbnail
        overflow="hidden"
        height={isBrannanLayout ? { _: pxToRem(284), sm2: pxToRem(503) } : { _: pxToRem(273), sm2: pxToRem(396), sm4: pxToRem(329) }}
        marginBottom={32}
        overrides={{
          PlaceholderRoot: {
            props: {
              width: '100%',
              paddingTop: '75%',
              backgroundColor: 'white',
              borderRadius: '12px !important',
            },
          },
        }}
      />
    </Flex>
  );
};

export { AccommodationsMapV2, AccommodationsMapV2Skeleton };
